/**
 * InventoryComponent - Manages entity inventory
 */

import { Component } from '../entities/Component';
import { ComponentType } from '../types/enums';
import { Item, ItemStack } from './Item';
import { ItemRegistry } from './ItemRegistry';

export class InventoryComponent extends Component {
  readonly type = ComponentType.Inventory;

  private slots: (ItemStack | null)[];
  private capacity: number;
  private selectedSlot: number = 0;

  // Callbacks
  public onItemAdded: ((item: Item, quantity: number, slot: number) => void) | null = null;
  public onItemRemoved: ((item: Item, quantity: number, slot: number) => void) | null = null;
  public onSelectionChanged: ((slot: number) => void) | null = null;

  constructor(capacity: number = 20) {
    super();
    this.capacity = capacity;
    this.slots = new Array(capacity).fill(null);
  }

  update(_deltaTime: number): void {
    // Inventory doesn't need per-frame updates
  }

  /** Add item to inventory */
  addItem(item: Item, quantity: number = 1): number {
    let remaining = quantity;

    // First, try to stack with existing items
    if (item.stackable) {
      for (let i = 0; i < this.capacity && remaining > 0; i++) {
        const slot = this.slots[i];
        if (slot && slot.item.id === item.id) {
          const canAdd = Math.min(remaining, item.maxStack - slot.quantity);
          slot.quantity += canAdd;
          remaining -= canAdd;
          this.onItemAdded?.(item, canAdd, i);
        }
      }
    }

    // Then, fill empty slots
    for (let i = 0; i < this.capacity && remaining > 0; i++) {
      if (this.slots[i] === null) {
        const addAmount = item.stackable ? Math.min(remaining, item.maxStack) : 1;
        this.slots[i] = { item, quantity: addAmount };
        remaining -= addAmount;
        this.onItemAdded?.(item, addAmount, i);
      }
    }

    return remaining; // Return overflow amount
  }

  /** Remove item from inventory */
  removeItem(itemId: string, quantity: number = 1): number {
    let toRemove = quantity;

    for (let i = this.capacity - 1; i >= 0 && toRemove > 0; i--) {
      const slot = this.slots[i];
      if (slot && slot.item.id === itemId) {
        const removed = Math.min(toRemove, slot.quantity);
        slot.quantity -= removed;
        toRemove -= removed;
        this.onItemRemoved?.(slot.item, removed, i);

        if (slot.quantity <= 0) {
          this.slots[i] = null;
        }
      }
    }

    return quantity - toRemove; // Return amount actually removed
  }

  /** Remove item at specific slot */
  removeItemAtSlot(slotIndex: number, quantity: number = 1): ItemStack | null {
    if (slotIndex < 0 || slotIndex >= this.capacity) return null;

    const slot = this.slots[slotIndex];
    if (!slot) return null;

    const removed = Math.min(quantity, slot.quantity);
    const result: ItemStack = { item: slot.item, quantity: removed };

    slot.quantity -= removed;
    this.onItemRemoved?.(slot.item, removed, slotIndex);

    if (slot.quantity <= 0) {
      this.slots[slotIndex] = null;
    }

    return result;
  }

  /** Check if has item */
  hasItem(itemId: string, quantity: number = 1): boolean {
    return this.getItemCount(itemId) >= quantity;
  }

  /** Count items */
  getItemCount(itemId: string): number {
    let count = 0;
    for (const slot of this.slots) {
      if (slot && slot.item.id === itemId) {
        count += slot.quantity;
      }
    }
    return count;
  }

  /** Get item at slot */
  getSlot(index: number): ItemStack | null {
    if (index < 0 || index >= this.capacity) return null;
    return this.slots[index] ?? null;
  }

  /** Get all slots */
  getAllSlots(): (ItemStack | null)[] {
    return [...this.slots];
  }

  /** Get non-empty slots */
  getItems(): ItemStack[] {
    return this.slots.filter((slot): slot is ItemStack => slot !== null);
  }

  /** Find first slot with item */
  findItemSlot(itemId: string): number {
    return this.slots.findIndex(slot => slot?.item.id === itemId);
  }

  /** Swap slots */
  swapSlots(from: number, to: number): void {
    if (from < 0 || from >= this.capacity || to < 0 || to >= this.capacity) return;
    const temp = this.slots[from] ?? null;
    this.slots[from] = this.slots[to] ?? null;
    this.slots[to] = temp;
  }

  /** Get selected slot index */
  getSelectedSlot(): number {
    return this.selectedSlot;
  }

  /** Get selected item */
  getSelectedItem(): ItemStack | null {
    return this.slots[this.selectedSlot] ?? null;
  }

  /** Set selected slot */
  setSelectedSlot(slot: number): void {
    if (slot >= 0 && slot < this.capacity) {
      this.selectedSlot = slot;
      this.onSelectionChanged?.(slot);
    }
  }

  /** Select next slot */
  selectNext(): void {
    this.setSelectedSlot((this.selectedSlot + 1) % Math.min(10, this.capacity));
  }

  /** Select previous slot */
  selectPrevious(): void {
    this.setSelectedSlot((this.selectedSlot - 1 + Math.min(10, this.capacity)) % Math.min(10, this.capacity));
  }

  /** Get capacity */
  getCapacity(): number {
    return this.capacity;
  }

  /** Get used slots count */
  getUsedSlots(): number {
    return this.slots.filter(s => s !== null).length;
  }

  /** Check if full */
  isFull(): boolean {
    return this.getUsedSlots() >= this.capacity;
  }

  /** Clear inventory */
  clear(): void {
    this.slots = new Array(this.capacity).fill(null);
  }

  /** Serialize for saving */
  toSaveData(): object {
    return {
      slots: this.slots.map(slot => {
        if (!slot) return null;
        return {
          itemId: slot.item.id,
          quantity: slot.quantity
        };
      }),
      selectedSlot: this.selectedSlot
    };
  }

  /** Deserialize from save data */
  fromSaveData(data: { slots: ({ itemId: string; quantity: number } | null)[]; selectedSlot: number }): void {
    this.clear();

    for (let i = 0; i < data.slots.length && i < this.capacity; i++) {
      const slotData = data.slots[i];
      if (slotData) {
        const item = ItemRegistry.get(slotData.itemId);
        if (item) {
          this.slots[i] = { item, quantity: slotData.quantity };
        }
      }
    }

    this.selectedSlot = data.selectedSlot;
  }
}
